import { Link } from "react-router-dom";
import { useEffect, useRef } from "react";
import {
  ArrowRight, Sparkles, Wrench, Users, Trees, Hammer, Zap, Package, Coffee, Truck, Boxes, CheckCircle2, HelpCircle,
} from "lucide-react";
import { SERVICES, SERVICES_COMPLEMENTAIRES } from "@/lib/content";

const ICONS = { Sparkles, Wrench, Users, Trees, Hammer, Zap, Package, Coffee, Truck, Boxes };
const Icon = ({ name, ...p }) => {
  const C = ICONS[name] || CheckCircle2;
  return <C {...p} />;
};

// Hook: observe .nx-reveal elements and add .is-visible when they enter viewport
function useScrollReveal() {
  const rootRef = useRef(null);
  useEffect(() => {
    const targets = (rootRef.current || document).querySelectorAll(".nx-reveal");
    if (!targets.length) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" }
    );
    targets.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return rootRef;
}

export default function Services() {
  const rootRef = useScrollReveal();
  return (
    <div data-testid="services-page" ref={rootRef}>
      {/* HERO */}
      <section className="bg-ink text-white relative overflow-hidden nx-hero-services" style={{ paddingTop: 200, paddingBottom: 120 }}>
        <div className="absolute inset-0 nx-hero-grid opacity-50"></div>
        <div
          className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20"
          style={{ background: "radial-gradient(circle, #00C8FF 0%, transparent 70%)" }}
        ></div>
        <div className="nx-container relative">
          <div className="max-w-3xl nx-fade-up">
            <span className="nx-overline">Nos expertises</span>
            <h1 className="text-white" style={{ marginTop: 24, marginBottom: 32 }}>
              Cinq pôles d'expertise. <span style={{ color: "#00C8FF" }}>Une seule signature.</span>
            </h1>
            <p className="text-mist" style={{ fontSize: 20, marginBottom: 0 }}>
              Toutes vos prestations coordonnées sous un seul contrat, sans perte de temps administrative. Chaque pôle est piloté par un chef de site dédié et suivi via des indicateurs clairs — pour une qualité constante sur la durée.
            </p>
          </div>
        </div>
      </section>

      {/* 5 SERVICES PRINCIPAUX — Full-bleed immersive photo + navy gradient */}
      {SERVICES.map((s, idx) => (
        <section
          key={s.id}
          id={s.id}
          className="relative overflow-hidden"
          style={{
            minHeight: 640,
            scrollMarginTop: 100,
            backgroundColor: "#0A2540",
            backgroundImage: `url(${s.image})`,
            backgroundSize: "cover",
            backgroundPosition: idx % 2 === 1 ? "left center" : "right center",
            backgroundRepeat: "no-repeat",
          }}
          data-testid={`service-detail-${s.id}`}
        >
          {/* Navy gradient overlay — fades from opaque (text side) to fully transparent (photo side) 
              to preserve agent visibility and authentic scene backgrounds */}
          <div
            className="absolute inset-0"
            aria-hidden="true"
            style={{
              background: idx % 2 === 1
                ? "linear-gradient(270deg, rgba(10,37,64,0.92) 0%, rgba(10,37,64,0.85) 25%, rgba(10,37,64,0.35) 45%, rgba(10,37,64,0) 60%)"
                : "linear-gradient(90deg, rgba(10,37,64,0.92) 0%, rgba(10,37,64,0.85) 25%, rgba(10,37,64,0.35) 45%, rgba(10,37,64,0) 60%)",
            }}
          />

          {/* Bottom mask — hides any image watermark/label baked into the source photo */}
          <div
            className="absolute"
            aria-hidden="true"
            style={{
              left: 0,
              right: 0,
              bottom: 0,
              height: 56,
              background: "linear-gradient(to top, rgba(10,37,64,1) 0%, rgba(10,37,64,1) 24px, rgba(10,37,64,0) 100%)",
            }}
          />

          <div className="nx-container relative" style={{ paddingTop: 96, paddingBottom: 96 }}>
            <div className={`grid grid-cols-1 lg:grid-cols-12 gap-14 items-center ${idx % 2 === 1 ? "lg:justify-items-end" : ""}`}>
              <div className={`lg:col-span-6 ${idx % 2 === 1 ? "lg:col-start-7" : ""}`}>
                <div className="flex items-center gap-4 nx-reveal" style={{ marginBottom: 24, "--nx-delay": "0ms" }}>
                  <div className="nx-icon-wrap"><Icon name={s.icon} size={26} strokeWidth={1.75} /></div>
                  <span className="nx-overline">Pôle · 0{idx + 1}</span>
                </div>
                <h2 className="text-white nx-reveal" style={{ marginBottom: 24, color: "#FFFFFF", "--nx-delay": "80ms" }}>{s.title}</h2>
                <p className="nx-reveal" style={{ marginBottom: 32, fontSize: 20, color: "rgba(255,255,255,0.85)", "--nx-delay": "180ms" }}>{s.tagline}</p>

                <ul style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {s.features.map((f, i) => (
                    <li
                      key={f}
                      className="flex items-start gap-3 nx-reveal nx-feature-item"
                      style={{ "--nx-delay": `${280 + i * 100}ms` }}
                    >
                      <CheckCircle2 size={20} strokeWidth={1.75} className="text-cyan shrink-0 mt-1" />
                      <span style={{ fontSize: 17, color: "rgba(255,255,255,0.92)" }}>{f}</span>
                    </li>
                  ))}
                </ul>

                {s.conclusion && (
                  <p
                    className="nx-reveal"
                    style={{ marginTop: 32, fontSize: 17, color: "rgba(255,255,255,0.75)", fontStyle: "italic", "--nx-delay": `${280 + s.features.length * 100 + 60}ms` }}
                  >
                    {s.conclusion}
                  </p>
                )}

                <div className="nx-reveal" style={{ marginTop: 40, "--nx-delay": `${280 + s.features.length * 100 + 160}ms` }}>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 text-cyan hover:text-white transition-colors"
                    style={{ fontFamily: "Exo 2", fontWeight: 600, fontSize: 15, letterSpacing: "0.02em" }}
                    data-testid={`service-cta-${s.id}`}
                  >
                    Planifier une visite technique <ArrowRight size={16} strokeWidth={2} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* LIEN VERS LA GALERIE RÉALISATIONS — univers distinct, accessible au clic */}
      <section className="bg-navy text-white" style={{ paddingTop: 100, paddingBottom: 100 }} data-testid="services-realisations-banner">
        <div className="nx-container">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between" style={{ gap: 40 }}>
            <div className="max-w-2xl">
              <span className="nx-overline nx-reveal" style={{ "--nx-delay": "0ms" }}>Nos réalisations</span>
              <h2 className="text-white nx-reveal" style={{ marginTop: 24, marginBottom: 20, "--nx-delay": "100ms" }}>
                Avant, après — la différence en un geste.
              </h2>
              <p className="nx-reveal" style={{ marginBottom: 0, fontSize: 18, color: "rgba(255,255,255,0.8)", "--nx-delay": "200ms" }}>
                Découvrez nos chantiers en images : rénovations, nettoyages fin de chantier, espaces verts. Chaque réalisation est documentée par un comparatif Avant / Après interactif.
              </p>
            </div>
            <div className="nx-reveal shrink-0" style={{ "--nx-delay": "300ms" }}>
              <Link to="/realisations" className="nx-btn nx-btn-primary" data-testid="services-realisations-link">
                Voir nos réalisations <ArrowRight size={18} strokeWidth={2} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES COMPLÉMENTAIRES */}
      <section className="bg-white nx-arch-grid-light" style={{ paddingTop: 120, paddingBottom: 120 }} data-testid="services-complementaires">
        <div className="nx-container">
          <div className="max-w-3xl">
            <span className="nx-overline nx-reveal" style={{ "--nx-delay": "0ms" }}>En complément</span>
            <h2
              className="nx-reveal"
              style={{
                marginTop: 24,
                marginBottom: 24,
                color: "#0D2B45",
                fontFamily: "Exo 2, sans-serif",
                fontWeight: 700,
                fontSize: "clamp(32px, 4vw, 48px)",
                lineHeight: 1.1,
                letterSpacing: "-0.01em",
                opacity: 1,
                position: "relative",
                zIndex: 2,
                "--nx-delay": "100ms",
              }}
              data-testid="services-complementaires-title"
            >
              Services complémentaires
            </h2>
            <p className="nx-reveal" style={{ marginBottom: 0, fontSize: 20, color: "#475569", "--nx-delay": "200ms" }}>
              Pour simplifier encore davantage votre quotidien, vous pouvez également nous confier la coordination de ces services annexes, grâce à notre organisation et à notre réseau de partenaires. Un contrat unique, une facturation regroupée, un pilotage centralisé.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 30, marginTop: 64 }}>
            {SERVICES_COMPLEMENTAIRES.map((c, i) => (
              <div
                key={c.id}
                className="nx-card nx-reveal"
                style={{ "--nx-delay": `${300 + i * 100}ms` }}
                data-testid={`complementaire-${c.id}`}
              >
                <div className="nx-icon-wrap"><Icon name={c.icon} size={26} strokeWidth={1.75} /></div>
                <h4 style={{ marginTop: 24, marginBottom: 16 }}>{c.title}</h4>
                <p style={{ fontSize: 16, marginBottom: 0 }}>{c.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final — orienté choix du pôle (cohérent avec la page Services) */}
      <section className="bg-navy text-white nx-cta-services-bg" style={{ paddingTop: 120, paddingBottom: 120 }} data-testid="services-cta">
        <div className="nx-container text-center">
          <span className="nx-overline">Un besoin spécifique&nbsp;?</span>
          <h2 className="text-white" style={{ marginTop: 24, marginBottom: 32, maxWidth: 860, marginLeft: "auto", marginRight: "auto" }}>
            Vous ne savez pas quel pôle correspond à votre besoin&nbsp;? Décrivez-nous votre site, on identifie la bonne solution.
          </h2>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="nx-btn nx-btn-primary" data-testid="services-final-cta">
              Décrire mon besoin <ArrowRight size={18} strokeWidth={2} />
            </Link>
            <Link to="/faq" className="nx-btn nx-btn-outline" data-testid="services-faq-link">
              <HelpCircle size={18} strokeWidth={1.75} /> Consulter les questions fréquentes
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
